/**
 * Setup & goal — the pure half (spec #47 §3, §3a, P4, P5).
 *
 * No React, no network: every function here is a straight input->output
 * mapping, which is why §9 puts them first in the build order and why the
 * five render states are testable without mounting anything.
 *
 * PROVENANCE IS BY VALUE COMPARISON, NOT ROW EXISTENCE (P4). The resolver
 * exposes `tenant_customized`, one bool for ten fields, which cannot
 * answer "which field". Comparison against the current system value can,
 * it makes per-field reset work, and it survives Phase 1b's delta columns
 * unchanged.
 *
 * WHAT IS NOT KNOWABLE YET IS SAID, NOT GUESSED (P5). Two gaps are open on
 * this build and both degrade to the fifth state rather than a confident
 * lie:
 *
 *   1. D1 (`field_provenance` on the resolver) has not landed, so the only
 *      per-field SYSTEM values a client can see are the two allowlists'
 *      `system_allowed_*`. For every other leash knob a tenant-sourced row
 *      leans on the settings-audit trail, which IS per-field
 *      (`diff_changes` keys its changes by field name).
 *   2. D2 (`author_role`, `source_proposal_id` on SettingsAudit) has not
 *      landed, so an audit row names a user but not the ROLE they acted
 *      in. A user id is not a role. The one case that is still knowable is
 *      the audit naming the person currently reading the form — their own
 *      role settles it. For anyone else the tag admits the gap and names
 *      who changed it, which tells more truth than "you" would have.
 *
 * Both read `author_role` / `source_proposal_id` the moment they appear,
 * so the honest degradation lifts on its own when the soul build lands.
 */

import type {
  AgentSettings,
  FieldPolicyEntry,
} from '../../../types/agentSettings';

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

/**
 * #42's four states plus the fifth this spec adds because the data forces
 * it. An invented attribution in an approvals product is worse than an
 * admitted gap.
 */
export type ProvenanceTag =
  | 'default'
  | 'you'
  | 'operator'
  | 'agent-proposed-you-approved'
  | 'source-unrecorded';

export interface Reader {
  role: 'tenant' | 'operator';
  user_id: string | null;
}

/** One field-level change from `GET /api/settings/audit`, flattened. */
export interface FieldAudit {
  field: string;
  old: unknown;
  new: unknown;
  changed_by: string | null;
  /** D2 — absent until the soul build lands. */
  author_role?: 'tenant' | 'operator' | null;
  /** D2 — absent until the soul build lands. */
  source_proposal_id?: string | null;
  created_at: string | null;
}

export interface Provenance {
  tag: ProvenanceTag;
  /** The user id an audit row names, when there is one. */
  attributedTo: string | null;
  proposalId: string | null;
  planVersion: number | null;
  reason: string;
}

export const PROVENANCE_LABELS: Record<ProvenanceTag, string> = {
  default: 'default',
  you: 'you',
  operator: 'operator',
  'agent-proposed-you-approved': 'agent-proposed · you approved',
  'source-unrecorded': 'this tenant · source unrecorded',
};

// ---------------------------------------------------------------------------
// Value comparison
// ---------------------------------------------------------------------------

/** Order-insensitive for lists: an allowlist is a set, not a sequence. */
export function valuesEqual(a: unknown, b: unknown): boolean {
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    const left = [...a].map(String).sort();
    const right = [...b].map(String).sort();
    return left.every((value, index) => value === right[index]);
  }
  if (
    a !== null &&
    b !== null &&
    typeof a === 'object' &&
    typeof b === 'object'
  ) {
    return JSON.stringify(sortKeys(a)) === JSON.stringify(sortKeys(b));
  }
  return a === b;
}

function sortKeys(value: object): unknown {
  const record = value as Record<string, unknown>;
  return Object.keys(record)
    .sort()
    .reduce<Record<string, unknown>>((acc, key) => {
      acc[key] = record[key];
      return acc;
    }, {});
}

function newest(audits: readonly FieldAudit[]): FieldAudit | null {
  if (audits.length === 0) return null;
  return [...audits].sort((a, b) =>
    String(b.created_at ?? '').localeCompare(String(a.created_at ?? ''))
  )[0];
}

function oldest(audits: readonly FieldAudit[]): FieldAudit | null {
  if (audits.length === 0) return null;
  return [...audits].sort((a, b) =>
    String(a.created_at ?? '').localeCompare(String(b.created_at ?? ''))
  )[0];
}

// ---------------------------------------------------------------------------
// resolveProvenance — the five states
// ---------------------------------------------------------------------------

export interface ResolveProvenanceInput {
  effective: unknown;
  /** The current system row's value, where the API exposes one. */
  systemValue?: unknown;
  /** Explicit override for "the API exposes a system value for this field". */
  systemValueKnown?: boolean;
  /** `source === 'system'`: no tenant override row exists at all. */
  sourceIsSystem?: boolean;
  /** Audit rows for THIS field only, any order. */
  audits?: readonly FieldAudit[];
  /** The approved plan's `lanes[].plan_set_fields` names this field (D3). */
  planSet?: boolean;
  planVersion?: number | null;
  reader: Reader;
}

export function resolveProvenance(
  input: ResolveProvenanceInput
): Provenance {
  const audits = input.audits ?? [];
  const known =
    input.systemValueKnown ?? input.systemValue !== undefined;

  const base: Provenance = {
    tag: 'source-unrecorded',
    attributedTo: null,
    proposalId: null,
    planVersion: null,
    reason: '',
  };

  if (known && valuesEqual(input.effective, input.systemValue)) {
    return {
      ...base,
      tag: 'default',
      reason: 'This is the platform value — nothing here was changed.',
    };
  }
  if (!known && input.sourceIsSystem) {
    return {
      ...base,
      tag: 'default',
      reason: 'This agent has no tenant override — the platform value stands.',
    };
  }

  if (input.planSet) {
    return {
      ...base,
      tag: 'agent-proposed-you-approved',
      planVersion: input.planVersion ?? null,
      reason: input.planVersion
        ? `Set by plan v${input.planVersion}, which you approved.`
        : 'Set by the plan you approved.',
    };
  }

  const latest = newest(audits);
  if (latest === null) {
    return {
      ...base,
      reason:
        'This differs from the platform value, but nothing recorded who ' +
        'changed it. Rather than guess, the form says so.',
    };
  }

  if (latest.source_proposal_id) {
    return {
      ...base,
      tag: 'agent-proposed-you-approved',
      attributedTo: latest.changed_by ?? null,
      proposalId: latest.source_proposal_id,
      reason: 'Proposed by an agent and approved by you.',
    };
  }

  if (latest.author_role === 'operator') {
    return {
      ...base,
      tag: 'operator',
      attributedTo: latest.changed_by ?? null,
      reason: 'Changed by an operator, recorded against their own user.',
    };
  }
  if (latest.author_role === 'tenant') {
    return {
      ...base,
      tag: 'you',
      attributedTo: latest.changed_by ?? null,
      reason: 'Changed in this workspace.',
    };
  }

  // D2 has not landed: the row names a user but not the role. The reader
  // is the one user whose role we do know.
  if (latest.changed_by && latest.changed_by === input.reader.user_id) {
    return {
      ...base,
      tag: input.reader.role === 'operator' ? 'operator' : 'you',
      attributedTo: latest.changed_by,
      reason: 'You changed this.',
    };
  }

  return {
    ...base,
    attributedTo: latest.changed_by ?? null,
    reason: latest.changed_by
      ? `Changed by ${latest.changed_by}; whether they acted as this ` +
        'workspace or as an operator was not recorded.'
      : 'This differs from the platform value, but nothing recorded who ' +
        'changed it.',
  };
}

// ---------------------------------------------------------------------------
// classifyCeiling — "locked, never hidden"
// ---------------------------------------------------------------------------

export type CeilingState =
  | 'editable'
  | 'plan_locked'
  | 'operator_only'
  | 'read_only_v1';

/**
 * A field the server said nothing about is NOT assumed editable. Rendering
 * a control this build cannot back would be exactly the lie §3 forbids.
 */
export function classifyCeiling(
  entry: FieldPolicyEntry | undefined | null
): CeilingState {
  if (!entry) return 'read_only_v1';
  if (entry.locked_by === 'operator') return 'operator_only';
  if (entry.locked_by === 'plan') {
    return entry.editable ? 'operator_only' : 'plan_locked';
  }
  if (!entry.editable) return 'read_only_v1';
  return 'editable';
}

export function isWritable(state: CeilingState): boolean {
  return state === 'editable' || state === 'operator_only';
}

// ---------------------------------------------------------------------------
// resetTargetFor — per-field reset, writing the system value back
// ---------------------------------------------------------------------------

export type ResetTarget =
  | { known: true; value: unknown }
  | { known: false; reason: string };

const SYSTEM_LIST_FIELDS: Record<string, keyof AgentSettings> = {
  allowed_action_types: 'system_allowed_action_types',
  allowed_platforms: 'system_allowed_platforms',
};

/**
 * Reset writes the platform value back through the SAME `PUT`; provenance
 * then flips to `default` by the comparison rule above — no new endpoint,
 * no delta columns, no second store (§3).
 *
 * Where the platform value is not knowable the answer is `known: false`
 * and the form hides the affordance. Offering a reset that wrote a guessed
 * number would be worse than not offering one.
 */
export function resetTargetFor(
  field: string,
  context: { agent: AgentSettings; audits: readonly FieldAudit[] }
): ResetTarget {
  const systemListField = SYSTEM_LIST_FIELDS[field];
  if (systemListField) {
    return {
      known: true,
      value: (context.agent[systemListField] as string[]) ?? [],
    };
  }

  if (context.agent.source !== 'tenant') {
    return {
      known: false,
      reason: 'Nothing to reset — this agent has no tenant override.',
    };
  }

  const first = oldest(context.audits);
  if (first && 'old' in first) {
    // The tenant row is seeded FROM the system definition, so the value
    // before the first recorded change is the platform value — recorded,
    // not guessed.
    return { known: true, value: first.old };
  }

  return {
    known: false,
    reason:
      'No record of the platform value for this field, so there is ' +
      'nothing safe to reset to. Whole-agent reset arrives with Phase 1b.',
  };
}

// ---------------------------------------------------------------------------
// §3a — specialist config provenance, by re-derivation
// ---------------------------------------------------------------------------

/**
 * Mirrors `marketing_suite/interview.py::BUDGET_PLATFORM_TO_SPECIALIST`.
 * Not exposed over the API (the budget answer names a CONNECTOR, and
 * nothing joins it to a specialist key on the wire), so replicated here
 * with the same discipline `treeWalk.ts` already applies to
 * `GOAL_HERO_METRIC`.
 */
export const BUDGET_PLATFORM_TO_SPECIALIST: Record<string, string> = {
  meta_ads: 'facebook_ads',
  google_ads: 'google_ads',
  linkedin_ads: 'linkedin_ads',
  tiktok_ads: 'tiktok_ads',
  snap_ads: 'snap_ads',
  bing_ads: 'bing_ads',
  amazon_ads: 'amazon_ads',
  pinterest_ads: 'pinterest_ads',
};

/**
 * The interview value a specialist config field would have come from.
 *
 * Deliberately compares against the interview ANSWER rather than
 * replaying `derive_configs`' fan-out membership: that fan-out is driven
 * by a catalog flag (`creative_generation`) the specialists endpoint does
 * not return, and a hand-maintained key list on the client is exactly the
 * drift the backend removed from itself. So the claim this makes is the
 * narrow, checkable one — "this matches your setup interview answer" —
 * never the wider "the interview wrote it here".
 */
function derivedFromAnswers(
  key: string,
  field: string,
  answers: Record<string, unknown>
): unknown {
  const text = (value: unknown): unknown =>
    typeof value === 'string' && value.trim()
      ? value.trim().slice(0, 1000)
      : undefined;

  switch (field) {
    case 'icp':
      return text(answers.icp);
    case 'brand_voice':
      return text(answers.brand_voice);
    case 'competitors': {
      const raw = answers.competitors;
      if (!Array.isArray(raw)) return undefined;
      const names = raw.map((c) => String(c).trim()).filter(Boolean);
      return names.length ? names.slice(0, 3) : undefined;
    }
    case 'seed_topics': {
      const raw = answers.seed_topics;
      if (!Array.isArray(raw)) return undefined;
      const topics = raw.map((t) => String(t).trim()).filter(Boolean);
      return topics.length ? topics.slice(0, 5) : undefined;
    }
    case 'monthly_budget': {
      const raw = answers.budget;
      if (!Array.isArray(raw)) return undefined;
      for (const entry of raw as Array<Record<string, unknown>>) {
        // A BLENDED budget is deliberately never copied onto a platform
        // agent — derive_configs' own rule, because N copies of one
        // number would let N x the cap through.
        if (entry?.scope !== 'platform') continue;
        if (
          BUDGET_PLATFORM_TO_SPECIALIST[String(entry.platform)] === key &&
          entry.amount != null
        ) {
          return Number(entry.amount);
        }
      }
      return undefined;
    }
    default:
      return undefined;
  }
}

function isUnset(value: unknown): boolean {
  if (value === undefined || value === null || value === '') return true;
  return Array.isArray(value) && value.length === 0;
}

export function specialistProvenance(input: {
  key: string;
  field: string;
  value: unknown;
  answers: Record<string, unknown> | null;
  reader: Reader;
}): Provenance {
  const base: Provenance = {
    tag: 'source-unrecorded',
    attributedTo: null,
    proposalId: null,
    planVersion: null,
    reason: '',
  };

  if (isUnset(input.value)) {
    return {
      ...base,
      tag: 'default',
      reason: 'Not set — this agent runs on the catalog default.',
    };
  }

  if (input.answers === null) {
    return {
      ...base,
      reason:
        'Set for this workspace, but there is no interview session left ' +
        'to compare it against, so where it came from is unrecorded.',
    };
  }

  const derived = derivedFromAnswers(input.key, input.field, input.answers);
  if (derived !== undefined && valuesEqual(input.value, derived)) {
    return {
      ...base,
      tag: 'agent-proposed-you-approved',
      reason: 'Matches your setup interview answer.',
    };
  }

  return {
    ...base,
    tag: input.reader.role === 'operator' ? 'operator' : 'you',
    attributedTo: input.reader.user_id,
    reason:
      input.reader.role === 'operator'
        ? 'Differs from the setup interview — changed by an operator.'
        : 'Differs from your setup interview answer — changed by hand.',
  };
}
